import React from 'react';
import PropTypes from 'prop-types';
import {
    Config,
    getCookie,
    getCustomerValues,
    setCustomerValues,
    getInputError,
    getFormErrors,
} from '@deporvillage/utils-library-component';
import Modal from '@deporvillage/modal-component';
import ForgotPasswordPopup from '@deporvillage/forgot-password-popup-component';
import RestorePassword from './RestorePassword';
import Customer from './utils/customer';

import styles from './RestorePasswordPopup.module.scss';

const RestorePasswordPopupExtended = ({ onRestorePasswordClick, onClose, token, redirect }) => {
    const [openRestorePassword, setOpenRestorePassword] = React.useState(true);
    const [openForgotPassword, setOpenForgotPassword] = React.useState(false);

    const toggleOpenRestorePassword = () => {
        setOpenRestorePassword(!openRestorePassword);
        onClose && onClose();
    };
    const toggleOpenForgotPassword = () => {
        setOpenForgotPassword(!openForgotPassword);
        onClose && onClose();
    };

    const handleClickPopUp = (e) => {
        onRestorePasswordClick && onRestorePasswordClick(e);
        /* istanbul ignore next */
        setOpenForgotPassword(true);
        setOpenRestorePassword(!openRestorePassword);
    };

    return (
        <div className={styles['restore-password-popup-extended']}>
            {openRestorePassword && (
                <RestorePasswordPopup
                    onClose={toggleOpenRestorePassword}
                    onClick={handleClickPopUp}
                    token={token}
                    redirect={redirect}
                />
            )}
            <ForgotPasswordPopup onClose={toggleOpenForgotPassword} isOpen={openForgotPassword} />
        </div>
    );
};

RestorePasswordPopupExtended.propTypes = {
    onRestorePasswordClick: PropTypes.func,
    onClose: PropTypes.func,
    token: PropTypes.string,
    redirect: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

const RestorePasswordPopup = ({ onClose, onClick, token, redirect }) => {
    const [customerData, setCustomerData] = React.useState(setCustomerValues(Customer));
    const [isVisible, setIsVisible] = React.useState(false);
    const [restorePasswordError, setRestorePasswordError] = React.useState(null);
    const [isValidRestorePasswordToken, setIsValidRestorePasswordToken] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const { api, country } = React.useContext(Config);

    const handleOnModalClose = () => {
        onClose && onClose();
    };

    const handleOnClick = (e) => {
        onClick && onClick(e);
    };

    // istanbul ignore next
    const handleOnInputChange = (input, form) => {
        const validateInput = {
            ...form,
            [input.id]: {
                ...input,
                error: getInputError(input, form),
            },
        };
        setCustomerData(validateInput);
        setRestorePasswordError(null);
    };

    // istanbul ignore next
    const getCustomerRestorePassword = async (api, token) => {
        const tokenMd5 = getCookie('token_md5');
        return fetch(`${api}/api/customer/restorepassword?token=${token}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                country: country.code,
                'dpv-session': getCookie('dpv-session'),
                ...(!tokenMd5 ? {} : { 'token-app': tokenMd5 }),
            },
            credentials: 'include',
        })
            .then((ans) => ans)
            .catch((e) => console.log(e));
    };

    // istanbul ignore next
    const handleOnValidToken = async (token) => {
        setLoading(true);
        const res = await getCustomerRestorePassword(api, token);
        if (res.status === 200) {
            setLoading(false);
            setIsVisible(true);
            setIsValidRestorePasswordToken(true);
        } else {
            setLoading(false);
            setIsVisible(true);
            setIsValidRestorePasswordToken(false);
        }
    };

    // istanbul ignore next
    const postCustomerRestorePassword = async (api, payload, token) => {
        const tokenMd5 = getCookie('token_md5');
        return fetch(`${api}/api/customer/restorepassword`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                country: country.code,
                'dpv-session': getCookie('dpv-session'),
                ...(!tokenMd5 ? {} : { 'token-app': tokenMd5 }),
            },
            credentials: 'include',
            body: JSON.stringify({ ...payload, token }),
        })
            .then((ans) => ans)
            .catch((e) => console.log(e));
    };

    // istanbul ignore next
    const handleOnRestorePassword = async (form) => {
        const [result, hasErrors] = getFormErrors(form);
        if (hasErrors) {
            setCustomerData(result);
        } else {
            setLoading(true);
            try {
                const res = await postCustomerRestorePassword(
                    api,
                    getCustomerValues(customerData),
                    token,
                );
                if (res.status === 200) {
                    setLoading(false);
                    !redirect || typeof redirect !== 'string'
                        ? window.location.reload()
                        : (window.location.href = redirect);
                } else {
                    const error = await res.json();
                    setLoading(false);
                    setRestorePasswordError(error);
                }
            } catch (e) {
                return e;
            }
        }
    };

    React.useEffect(() => {
        if (token) {
            handleOnValidToken(token);
        }
    }, [token]);

    return (
        <div
            className={`${styles['restore-password-popup-component']} ${
                !onClose ? styles['fixed-close'] : ''
            }`}
        >
            <Modal onClose={handleOnModalClose}>
                <div
                    className={`${styles['restore-password-wrapper']} ${
                        loading ? styles['loading'] : ''
                    } ${isVisible ? styles[''] : styles['not-visible']}`}
                >
                    {isVisible && (
                        <RestorePassword
                            validToken={isValidRestorePasswordToken}
                            customerData={customerData}
                            error={restorePasswordError}
                            onChange={handleOnInputChange}
                            onRestorePassword={handleOnRestorePassword}
                            onClick={handleOnClick}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

RestorePasswordPopup.propTypes = {
    token: PropTypes.string,
    redirect: PropTypes.string,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onClick: PropTypes.func,
    redirect: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default RestorePasswordPopup;
export { RestorePasswordPopupExtended };
